// 3rd Party
import styled from 'styled-components';

// Components
import {
  FlexCol,
  FlexRow,
  H2,
  StyledFormItem,
} from 'common/styles/oc-styled-components';

// Other
import { BREAKPOINTS, colors } from 'common/styles/style-constants';
import { Alert, Form, Icon, Input, Radio } from 'opencare-crown';

// Exports
export const ConfirmationSubHeading = styled(H2)`
  border-bottom: 1px solid #d0cdd0;
  font-size: 14px;
  text-align: left;
`;

export const ResponsiveInputRow = styled(FlexCol)`
  && {
    @media (min-width: ${BREAKPOINTS.tabletMin}px) {
      flex-direction: row;
    }
  }
`;

export const NameBirthContainer = styled(FlexCol)`
  && {
    ${props =>
      (props.flex
        ? `@media (min-width: ${BREAKPOINTS.tabletMin}px) {
          & > :last-child {
            width: 100%;
          }
        }`
        : null)}
  }
`;

export const PrimaryMemberSwitch = styled(Radio.Button)`
  && {
    height: auto;
    padding: 15px 10px;
    text-align: center;
    width: 50%;
    border-radius: ${props => props.border} !important;

    > span {
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
    > .ant-radio-button {
      height: auto;
    }
    &.ant-radio-button-wrapper {
      cursor: pointer;
    }
    &.ant-radio-button-wrapper:not(.ant-radio-button-wrapper-disabled) {
      &:hover {
        color: ${colors.blue};
      }
    }
    &.ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
      background: ${colors.blue};
      border-color: ${colors.blue};
      box-shadow: none;
      color: ${colors.white};
      font-weight: 600;
      &:active {
        background: ${colors.blue};
        border-color: ${colors.blue};
        box-shadow: none;
        color: ${colors.white};
      }
      &:hover {
        background: ${colors.blue};
        border-color: ${colors.blue};
        box-shadow: none;
        color: ${colors.white};
      }
    }
    @media (max-width: ${BREAKPOINTS.mobileMax}px) {
      padding: 8px 0;
    }
  }
`;

export const StyledAlert = styled(Alert)`
  && {
    align-items: ${props => props.align};
    background-color: rgba(80, 124, 117, 0.05);
    border: 1px solid ${colors.sage7};
    border-radius: 3px;
    cursor: ${props => props.cursor};
    display: ${props => props.display};
    font-size: ${props => props.fontSize};
    font-weight: ${props => props.fontWeight};
    height: ${props => props.height || '60px'};
    justify-content: ${props => props.justify};
    margin: ${props => props.margin};
    text-align: ${props => props.textAlign};
    transition: all 0.2s;

    .ant-alert {
      padding: 10px 45px;
    }
    .ant-alert-message {
      color: ${colors.sage7};
      width: 100%;
    }
    .ant-alert-icon {
      top: auto;
      margin-right: 5px;
    }
  }
`;

export const MultiFloatingInputRow = styled(FlexRow)`
  justify-content: space-between;

  & > * {
    width: calc(
      ${props => 100 / props.itemcount}% -
        ${props => 8 * (props.itemcount - 1) / props.itemcount}px
    );
  }
`;

export const StyledSelectContainer = styled(Form.Item)`
  && {
    display: inline-block;
    margin-bottom: 0;
    width: calc(
      ${props => 100 / props.itemcount}% -
        ${props => 8 * (props.itemcount - 1) / props.itemcount}px
    );
    height: 60px;
    position: relative;

    .ant-calendar-picker-input {
      height: 60px;
    }
    .ant-select-selection {
      height: 60px;
    }
    .ant-select-selection__rendered {
      align-items: center;
      display: flex;
      height: 60px;
      justify-content: center;
    }
  }
`;

export const StyledInput = styled(Input)`
  && {
    font-size: ${props => props.fontSize};
    text-transform: ${props => props.capitalize === 'true' && 'capitalize'};
    height: ${props => props.height || '60px'};
    margin: ${props => props.margin || 0};
    padding: ${props => props.padding};
    text-align: ${props => props.textalign};
    width: ${props => props.width};
    max-width: 100%;
    border-radius: 10px;
    box-shadow: inset 0px 2px 3px rgba(0, 0, 0, 0.1);
    border: 1px solid
      ${props => (props.error === 'true' ? 'red' : colors.gray6)};

    &::placeholder {
      color: ${colors.gray8};
    }

    &:not(:disabled) {
      &:active,
      &:focus {
        border-color: ${colors.blue};
        box-shadow: 0 0 3px ${colors.blue};
        outline: none !important;
      }

      &:hover {
        border-color: ${colors.blue};
        box-shadow: inset 0px 2px 3px rgba(0, 0, 0, 0.05);
      }
    }
  }
`;

export const StyledLock = styled(Icon)`
  && {
    bottom: ${props => props.bottom};
    color: ${colors.ash};
    font-size: ${props => props.size || '12px'};
    height: 12px;
    margin: ${props => props.margin};
    position: relative;
    width: 18px;
  }
`;

export const StyledSpan = styled.span`
  display: inline-block;
  text-align: center;
  width: 8px;
`;

export const StyledIcon = styled(Icon)`
  && {
    font-size: ${props => props.fontSize || 'inherit'};
    margin: ${props => props.margin || 0};
    &.ant-alert-icon {
      top: auto;
      color: ${props => props.color || colors.darkBlue};
    }
  }
`;

export const StyledTextArea = styled(Input.TextArea)`
  && {
    font-size: 12px;
    padding: 10px 15px;

    &:focus,
    &:active,
    &:hover {
      border: 1px solid ${colors.darkBlue};
      box-shadow: none;
    }

    &::placeholder {
      color: ${colors.gray7};
    }
  }
`;

export const Link = styled.a`
  border-bottom: 1px solid ${colors.blue};
  color: ${colors.blue};
  transition: border 0.1s;

  &:hover,
  &:focus {
    color: ${colors.blue};
    border-color: transparent;
  }
`;

export const FlexFormItem = styled(StyledFormItem)`
  && {
    .ant-form-item-children {
      display: flex;
    }
  }
`;

export const StepLoading = styled(Icon)`
  && {
    display: block;
    margin: 50px auto 30px;
    font-size: 24px;
  }
`;
